import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useSelector } from "react-redux";
import LanguageContext from "../../helper/LanguageContext";
import { callAPI } from "../../helper/apiUtils";
import { BASE_URL } from "../../helper/constants";
import { $crud } from "../../helper/CrudFactory";
import { convertPosition } from "../../helper/convertor";
import {
  UPDATE_PROJECT_ACTION_FIRE,
  SET_IN_ACTIVITY,
  SET_OPEN_SIDE_BAR,
  TOGGLE_LATEST_MSG,
  TOGGLE_TODO_VIEW,
  store,
} from "../../store";
import ApiLoaderOverlay from "../../components/ApiLoaderOverlay";
import CompanyNotificationSettings from "../../components/CompanyNotificationSettings";
import { SettingsNote, SettingsRow, SettingsSection, SettingsStack } from "../../components/settings/SettingsList";
import {
  getArticleTranslationUiOptions,
  normalizeArticleTranslationLanguages,
  sortArticleTranslationLangCodes,
} from "../../helper/articleTranslationLanguagesConfig";
import { getAvailableLanguages } from "../../Languages";
import {
  ADMIN_CENTER_SECTIONS,
  CompanyAdminEmbed,
  adminCenterGroups,
  adminSectionKey,
  readMediaExportFlag,
  storeMediaExportFlag,
} from "./CompanyAdmin";
import "./CompanySetting.css";

/*
 * Company settings (mobile CompanySettingComponent, Kjuup Pro look): captioned
 * groups "Verwaltung" and "Unternehmen" plus the leave row for employees. Each
 * row shows only for the roles the app shows it to. Kjuup+ company admins also
 * get the Admin-Panel sections (the app's Admin Center: Firma · Personen
 * hinzufügen · Einstellungen); a section opens in place, named by the URL hash.
 */

// Company languages: same as personal profile settings (DE first, then EN, then the rest)
const COMPANY_LANGUAGES = [
  { code: "de", name: "Deutsch" },
  { code: "gsw", name: "Schweizerdeutsch" },
  { code: "wae", name: "Walliserdeutsch" },
  { code: "en", name: "English" },
  { code: "fr", name: "Français" },
  { code: "it", name: "Italiano" },
  { code: "es", name: "Español" },
  { code: "pt", name: "Português" },
  { code: "ru", name: "Русский" },
  { code: "pl", name: "Polski" },
  { code: "nl", name: "Nederlands" },
  { code: "tr", name: "Türkçe" },
  { code: "el", name: "Ελληνικά" },
  { code: "cs", name: "Čeština" },
  { code: "hu", name: "Magyar" },
  { code: "ro", name: "Română" },
  { code: "bg", name: "Български" },
  { code: "hr", name: "Hrvatski" },
  { code: "sl", name: "Slovenščina (Slovenian)" },
  { code: "sk", name: "Slovenčina (Slovak)" },
  { code: "sv", name: "Svenska" },
  { code: "no", name: "Norsk" },
  { code: "da", name: "Dansk" },
  { code: "fi", name: "Suomi" },
  { code: "zh", name: "中文" },
  { code: "hi", name: "हिन्दी" },
  { code: "ar", name: "العربية" },
  { code: "ja", name: "日本語" },
  { code: "ko", name: "한국어" },
  { code: "vi", name: "Tiếng Việt" },
  { code: "id", name: "Bahasa Indonesia" },
  { code: "ms", name: "Bahasa Melayu" },
  { code: "bn", name: "বাংলা" },
  { code: "ta", name: "தமிழ்" },
  { code: "te", name: "తెలుగు" },
  { code: "mr", name: "मराठी" },
];

const ROLE_SUPER_ADMIN = "ROLE_COMPANY_SUPER_ADMIN";
const ROLE_DEPT_ADMIN = "ROLE_COMPANY_DEPARTMENT_ADMIN";
const POSITION_ADMIN = "COMPANY ADMIN";
const POSITION_DEPT_ADMIN = "COMPANY_DEPARTMENT_ADMIN";
const POSITION_EMPLOYEE = "EMPLOYEE";

const positionOf = (person) => String(person?.position || "").trim().toUpperCase();

/**
 * What the viewer may do in this company (GET /company/:id/profile). Company
 * admins hold ROLE_COMPANY_SUPER_ADMIN with the position "COMPANY ADMIN".
 * Ordinary employees hold ROLE_COMPANY_ADMIN with "EMPLOYEE" (backend
 * assignAdmin / join requests), so that role never opens an admin row.
 */
function accessOf(profile) {
  const role = profile?.role || null;
  const position = positionOf(profile);
  const isCompanyAdmin = role === ROLE_SUPER_ADMIN;
  return {
    isCompanyAdmin,
    isAdminPosition: position === POSITION_ADMIN,
    isDeptAdmin: !isCompanyAdmin && (role === ROLE_DEPT_ADMIN || position === POSITION_DEPT_ADMIN),
    isEmployee: position === POSITION_EMPLOYEE,
  };
}

// Rows of GET /company/:id/admin: company admins are everyone but employees and department admins.
const isCompanyAdminEntry = (person) => {
  const position = positionOf(person);
  return position !== POSITION_EMPLOYEE && position !== POSITION_DEPT_ADMIN;
};
const isDeptAdminEntry = (person) => positionOf(person) !== POSITION_EMPLOYEE;

const fullNameOf = (person) =>
  String(person?.fullName || [person?.firstName, person?.lastName].filter(Boolean).join(" ")).trim();

// As in the app: the first name, the last name or the full name starts with the query.
function nameMatches(person, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const first = String(person?.firstName || "").trim().toLowerCase();
  const last = String(person?.lastName || "").trim().toLowerCase();
  return (
    first.startsWith(q) ||
    last.startsWith(q) ||
    `${first} ${last}`.startsWith(q) ||
    fullNameOf(person).toLowerCase().startsWith(q)
  );
}

function deptLine(person) {
  if (!person?.department_name) return "";
  return person.location_name && person.location_name !== "null"
    ? `${person.department_name}/${person.location_name}`
    : person.department_name;
}

function initialsOf(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

const LANGUAGE_META = new Map(getAvailableLanguages().map((lang) => [lang.code, lang]));

function languageMatches(lang, query) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const meta = LANGUAGE_META.get(lang.code) || {};
  return [lang.name, lang.code, meta.name, meta.searchTerms]
    .filter(Boolean)
    .some((text) => String(text).toLowerCase().includes(q));
}

function PersonAvatar({ person }) {
  const [failed, setFailed] = useState(false);
  if (person?.avatar && !failed) {
    return (
      <img
        className="company-setting__avatar"
        src={BASE_URL + person.avatar}
        alt=""
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className="company-setting__avatar company-setting__avatar--initials" aria-hidden="true">
      {initialsOf(fullNameOf(person)) || "?"}
    </span>
  );
}

function PickMark({ on }) {
  return (
    <span className={`material-symbols-outlined company-setting__mark${on ? " is-on" : ""}`} aria-hidden="true">
      {on ? "check_circle" : "radio_button_unchecked"}
    </span>
  );
}

function PanelSearch({ value, onChange, placeholder }) {
  return (
    <div className="company-setting__search">
      <span className="material-symbols-outlined company-setting__search-icon" aria-hidden="true">
        search
      </span>
      <input
        type="text"
        className="company-setting__search-input"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function PanelLoading() {
  return <div className="company-setting__loading" role="status" aria-busy="true" />;
}

/** Search and the people with their admin check (the app's members panel). */
function MembersPanel({ items, loading, search, onSearch, searchPlaceholder, emptyText, onPick }) {
  let list;
  if (items.length) {
    list = items.map((item) => (
      <button
        key={item.key}
        type="button"
        role="checkbox"
        aria-checked={item.on}
        className={`company-setting__person${item.on ? " is-on" : ""}`}
        onClick={() => onPick(item.person)}
      >
        <PersonAvatar person={item.person} />
        <span className="company-setting__person-text">
          <span className="company-setting__person-name">{item.name}</span>
          {item.sub ? <span className="company-setting__person-sub">{item.sub}</span> : null}
        </span>
        <PickMark on={item.on} />
      </button>
    ));
  } else if (loading) {
    list = <PanelLoading />;
  } else {
    list = <p className="company-setting__empty">{emptyText}</p>;
  }
  return (
    <div className="company-setting__panel">
      <PanelSearch value={search} onChange={onSearch} placeholder={searchPlaceholder} />
      <div className="company-setting__list">{list}</div>
    </div>
  );
}

/** Search and the people with a remove button (the app's remove panel). */
function RemovePanel({ items, loading, search, onSearch, searchPlaceholder, emptyText, removeLabel, onRemove }) {
  let list;
  if (items.length) {
    list = items.map((item) => (
      <div key={item.key} className="company-setting__person">
        <PersonAvatar person={item.person} />
        <span className="company-setting__person-text">
          <span className="company-setting__person-name">{item.name}</span>
          {item.sub ? <span className="company-setting__person-sub">{item.sub}</span> : null}
        </span>
        <button
          type="button"
          className="company-setting__remove"
          title={removeLabel}
          aria-label={item.name ? `${removeLabel}: ${item.name}` : removeLabel}
          onClick={() => onRemove(item.person)}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            person_remove
          </span>
        </button>
      </div>
    ));
  } else if (loading) {
    list = <PanelLoading />;
  } else {
    list = <p className="company-setting__empty">{emptyText}</p>;
  }
  return (
    <div className="company-setting__panel">
      <PanelSearch value={search} onChange={onSearch} placeholder={searchPlaceholder} />
      <div className="company-setting__list">{list}</div>
    </div>
  );
}

/** The company languages with a search; a pick saves at once (the app's picker sheet). */
function LanguagePanel({ value, label, search, onSearch, searchPlaceholder, emptyText, onPick }) {
  const options = COMPANY_LANGUAGES.filter((lang) => languageMatches(lang, search));
  return (
    <div className="company-setting__panel">
      <PanelSearch value={search} onChange={onSearch} placeholder={searchPlaceholder} />
      <div className="company-setting__list" role="listbox" aria-label={label}>
        {options.length ? (
          options.map((lang) => {
            const selected = lang.code === value;
            return (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={selected}
                className={`company-setting__option${selected ? " is-selected" : ""}`}
                onClick={() => onPick(lang)}
              >
                <span className="company-setting__option-label">{lang.name}</span>
                {selected ? (
                  <span className="material-symbols-outlined company-setting__option-check" aria-hidden="true">
                    check
                  </span>
                ) : null}
              </button>
            );
          })
        ) : (
          <p className="company-setting__empty">{emptyText}</p>
        )}
      </div>
    </div>
  );
}

/** Target languages of the company's articles: check rows and Save (at least one stays on). */
function ArticleLanguagesPanel({ note, selection, onToggle, onSave, saving, saveLabel }) {
  const options = getArticleTranslationUiOptions();
  return (
    <div className="company-setting__panel">
      {note ? <p className="company-setting__panel-note">{note}</p> : null}
      <div className="company-setting__list">
        {selection == null ? (
          <PanelLoading />
        ) : (
          options.map((lang) => {
            const on = selection.includes(lang.code);
            return (
              <button
                key={lang.code}
                type="button"
                role="checkbox"
                aria-checked={on}
                className={`company-setting__option${on ? " is-selected" : ""}`}
                onClick={() => onToggle(lang.code)}
              >
                <span className="company-setting__option-label">{`${lang.code.toUpperCase()} — ${lang.nativeName}`}</span>
                <PickMark on={on} />
              </button>
            );
          })
        )}
      </div>
      <button
        type="button"
        className={`kj-btn kj-btn--primary kj-btn--block${saving ? " kj-btn--loading" : ""}`}
        disabled={saving || selection == null}
        onClick={onSave}
      >
        {saveLabel}
      </button>
    </div>
  );
}

function SettingsSkeleton() {
  return (
    <div className="company-setting__skeleton" aria-hidden="true">
      <span className="company-setting__skeleton-caption" />
      <div className="kj-settings-card">
        {[0, 1, 2].map((row) => (
          <div key={row} className="company-setting__skeleton-row">
            <span className="company-setting__skeleton-tile" />
            <span className="company-setting__skeleton-line" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CompanySetting(props) {
  const companyId = props.match.params.id;
  const rawDeptId = props.match.params.deptId;
  const departmentId = rawDeptId && rawDeptId !== "undefined" && rawDeptId !== "null" ? rawDeptId : null;
  const isKjuupHint = !!props.location?.state?.isKjuup;
  const user = useSelector(({ user }) => user);
  const history = useHistory();
  const { isGerm, strings } = useContext(LanguageContext);
  const tr = (key, de, en) => strings[key] || (isGerm ? de : en);

  const [company, setCompany] = useState(null); // null while loading, false when it failed
  const [reloadKey, setReloadKey] = useState(0);
  const [language, setLanguage] = useState("de");
  const [open, setOpen] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [coworkers, setCoworkers] = useState([]);
  const [coworkersLoading, setCoworkersLoading] = useState(false);
  const [articleLangs, setArticleLangs] = useState(null);
  const [articleLangsSaving, setArticleLangsSaving] = useState(false);

  // Kjuup+ companies: green confirm buttons (App.js reads data-kjuup-plus).
  const isKjuupCompany = isKjuupHint || !!(company && company.isKjuup);
  useEffect(() => {
    if (isKjuupCompany) {
      document.body.classList.add("kjuup-plus");
      document.body.setAttribute("data-kjuup-plus", "true");
      document.documentElement.classList.add("kjuup-plus");
      document.documentElement.setAttribute("data-kjuup-plus", "true");
    } else {
      document.body.classList.remove("kjuup-plus");
      document.body.removeAttribute("data-kjuup-plus");
      document.documentElement.classList.remove("kjuup-plus");
      document.documentElement.removeAttribute("data-kjuup-plus");
    }
    return () => {
      document.body.classList.remove("kjuup-plus");
      document.body.removeAttribute("data-kjuup-plus");
      document.documentElement.classList.remove("kjuup-plus");
      document.documentElement.removeAttribute("data-kjuup-plus");
    };
  }, [isKjuupCompany]);

  const loadSetting = useCallback(async () => {
    const { data } = await callAPI(`/company/${companyId}/setting`, {}, "GET");
    // Flat { language } or nested { setting: { language } }.
    const raw = data && typeof data.setting !== "undefined" ? data.setting : data || {};
    setLanguage(raw?.language ?? data?.language ?? "de");
  }, [companyId]);

  // Department admins see their own department only (the app's GetAdminRight / getRemoveEmployee).
  const deptFilterFor = useCallback(
    (profile) =>
      !accessOf(profile).isCompanyAdmin && departmentId ? `?deptLocation=${encodeURIComponent(departmentId)}` : "",
    [departmentId]
  );

  const loadAdmins = useCallback(
    async (profile) => {
      const access = accessOf(profile);
      if (!access.isCompanyAdmin && !access.isDeptAdmin) return;
      setAdminsLoading(true);
      try {
        const { data } = await callAPI(`/company/${companyId}/admin${deptFilterFor(profile)}`, {}, "GET");
        setAdmins(Array.isArray(data?.coworker) ? data.coworker : []);
      } finally {
        setAdminsLoading(false);
      }
    },
    [companyId, deptFilterFor]
  );

  const loadCoworkers = useCallback(
    async (profile) => {
      setCoworkersLoading(true);
      try {
        const { data } = await callAPI(`/company/${companyId}/coworker${deptFilterFor(profile)}`, {}, "GET");
        setCoworkers(Array.isArray(data?.coworkers) ? data.coworkers : []);
      } finally {
        setCoworkersLoading(false);
      }
    },
    [companyId, deptFilterFor]
  );

  const loadArticleLangs = useCallback(async () => {
    const { data } = await callAPI(`/company/${companyId}/articleTranslationLanguages`, {}, "GET");
    // The backend answers { availableLanguages, selectedLanguages }.
    const stored = data?.status ? data.selectedLanguages ?? data.languages : null;
    setArticleLangs(normalizeArticleTranslationLanguages(stored));
  }, [companyId]);

  useEffect(() => {
    let cancelled = false;
    setCompany(null);
    setOpen(null);
    setSearch("");
    setAdmins([]);
    setCoworkers([]);
    setArticleLangs(null);
    (async () => {
      const [profileRes] = await Promise.all([
        callAPI(`/company/${companyId}/profile`, {}, "GET"),
        loadSetting().catch(() => {}),
      ]);
      if (cancelled) return;
      const profile = profileRes?.data?.status ? profileRes.data.profile : null;
      setCompany(profile || false);
      // The admin list also counts the other company admins (the "remove my admin rights" row).
      if (profile) loadAdmins(profile).catch(() => {});
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, reloadKey, loadSetting, loadAdmins]);

  const { isCompanyAdmin, isAdminPosition, isDeptAdmin, isEmployee } = accessOf(company || null);
  const hasDept = !!departmentId;
  const otherAdmins = admins.filter(isCompanyAdminEntry).length;

  // Admin-Panel sections: Kjuup+ company admins (the app's viewerAssignsCompanySeats).
  const navState = props.location?.state || null;
  const stateMediaFlag = navState && typeof navState.mediaExportEnabled === "boolean" ? navState.mediaExportEnabled : null;
  useEffect(() => {
    if (stateMediaFlag != null) storeMediaExportFlag(companyId, stateMediaFlag);
  }, [stateMediaFlag, companyId]);
  const mediaExportEnabled = stateMediaFlag != null ? stateMediaFlag : readMediaExportFlag(companyId);
  const runsAdminCenter =
    !!company && !!company.isKjuup && company.stripeFeatureEnabled !== false && (isCompanyAdmin || isAdminPosition);
  const adminGroups = runsAdminCenter ? adminCenterGroups(company, mediaExportEnabled, { teams: isCompanyAdmin }) : [];
  // Microsoft Teams approval: every company admin, with or without the Admin-Panel sections.
  const teamsOutsideAdminCenter = isCompanyAdmin && !runsAdminCenter;
  const requestedSection = adminSectionKey(props.location?.hash);
  const activeSectionKey =
    requestedSection &&
    (adminGroups.some((group) => group.keys.includes(requestedSection)) ||
      (requestedSection === "teams" && teamsOutsideAdminCenter))
      ? requestedSection
      : null;
  const activeSection = activeSectionKey ? ADMIN_CENTER_SECTIONS[activeSectionKey] : null;
  const settingsPath = props.location?.pathname || `/company-setting/${companyId}/${rawDeptId}`;

  const openAdminSection = (key) => {
    setOpen(null);
    history.push({
      pathname: settingsPath,
      hash: `#${ADMIN_CENTER_SECTIONS[key].hash}`,
      state: { ...(navState || {}), adminFromIndex: true },
    });
  };

  const backToSettings = () => {
    if (navState && navState.adminFromIndex) {
      history.goBack();
      return;
    }
    history.push({ pathname: settingsPath, state: navState || undefined });
  };

  const goCockpitCompany = (tab = "company") => {
    store.dispatch({ type: TOGGLE_TODO_VIEW, payload: false });
    store.dispatch({ type: TOGGLE_LATEST_MSG, payload: false });
    store.dispatch({ type: SET_IN_ACTIVITY, status: false });
    store.dispatch({ type: SET_OPEN_SIDE_BAR, payload: "cockpit" });
    history.push({
      pathname: "/cockpit",
      search: tab === "company" ? `?tab=company&company=${encodeURIComponent(companyId)}` : `?tab=${tab}`,
    });
  };

  const notify = (type, message) => {
    if (message) $crud.notify({ type, message });
  };
  const failMessage = (data) =>
    data?.message ||
    tr("something_went_wrong", "Etwas ist schief gelaufen, bitte versuche es erneut", "Something went wrong, please try again");

  // $crud.confirm rejects on cancel.
  const ask = async (title, textContent) => {
    try {
      await $crud.confirm({
        title: title || "",
        textContent,
        options: { ok: strings.ok || "OK", cancel: tr("cancel", "Abbrechen", "Cancel") },
      });
      return true;
    } catch (_) {
      return false;
    }
  };

  const withBusy = async (task) => {
    setBusy(true);
    try {
      return await task();
    } finally {
      setBusy(false);
    }
  };

  const toggleRow = (key, onOpen) => {
    setSearch("");
    if (open === key) {
      setOpen(null);
      return;
    }
    setOpen(key);
    if (onOpen) Promise.resolve(onOpen()).catch(() => {});
  };

  const toggleCompanyAdmin = async (person) => {
    const makeAdmin = !isCompanyAdminEntry(person);
    if (!(await ask(strings.confirm, makeAdmin ? strings.makeAdmin : strings.makeEmp))) return;
    await withBusy(async () => {
      const { data } = await callAPI(
        `/company/${companyId}/admin/${person.id}?position=${makeAdmin ? "companyAdmin" : "employee"}`,
        { user: person.id, companyId },
        "GET"
      );
      if (!data?.status) {
        notify("error", failMessage(data));
        return;
      }
      notify("success", data.message);
      setAdmins((list) =>
        list.map((u) => (u.id === person.id ? { ...u, position: makeAdmin ? POSITION_ADMIN : POSITION_EMPLOYEE } : u))
      );
    });
  };

  const toggleDeptAdmin = async (person) => {
    if (!departmentId) return;
    const makeAdmin = !isDeptAdminEntry(person);
    if (!(await ask(strings.confirm, makeAdmin ? strings.add_dep : strings.dep_remove))) return;
    await withBusy(async () => {
      const dept = encodeURIComponent(departmentId);
      const { data } = makeAdmin
        ? await callAPI(`/company/${companyId}/deptAdmin/${person.id}?position=departmentadmin&deptLocation=${dept}`, null, "GET")
        : await callAPI(`/company/${companyId}/deptAdmin/${person.id}?deptLocation=${dept}`, null, "DELETE");
      if (!data?.status) {
        notify("error", failMessage(data));
        return;
      }
      notify("success", data.message);
      setAdmins((list) =>
        list.map((u) =>
          u.id === person.id ? { ...u, position: makeAdmin ? POSITION_DEPT_ADMIN : POSITION_EMPLOYEE } : u
        )
      );
    });
  };

  const removeMyAdminRights = async () => {
    if (!(await ask(strings.confirm, strings.remove_my_self))) return;
    const outcome = await withBusy(async () => {
      const { data } = await callAPI(`/company/${companyId}/admin/`, { id: companyId }, "DELETE");
      // The backend answers status:false also when it removed the rights, so the profile decides.
      const profileRes = await callAPI(`/company/${companyId}/profile`, {}, "GET");
      const profile = profileRes?.data?.status ? profileRes.data.profile : null;
      const access = accessOf(profile);
      return { data, removed: !!profile && !access.isCompanyAdmin && !access.isAdminPosition };
    });
    if (!outcome.removed) {
      notify("error", failMessage(outcome.data));
      return;
    }
    notify("success", outcome.data?.message);
    goCockpitCompany();
  };

  const removeMyDeptAdminRights = async () => {
    if (!(await ask(strings.confirm, strings.my_self_dep_remove))) return;
    const dept = departmentId ? `?deptLocation=${encodeURIComponent(departmentId)}` : "";
    const data = await withBusy(async () => (await callAPI(`/company/${companyId}/deptAdmin/${user?.id}${dept}`, null, "DELETE")).data);
    if (!data?.status) {
      notify("error", failMessage(data));
      return;
    }
    notify("success", data.message);
    goCockpitCompany();
  };

  const removeEmployee = async (person) => {
    const text = String(strings.do_you_really_want_to_remove || "").replace("username", fullNameOf(person));
    if (!(await ask(strings.remove_member, text))) return;
    await withBusy(async () => {
      const { data } = await callAPI(`/company/${person.id}/remove/${companyId}`, { id: person.id, companyId }, "GET");
      if (!data?.status) {
        notify("error", failMessage(data));
        return;
      }
      notify("success", data.message);
      setCoworkers((list) => list.filter((u) => u.id !== person.id));
      setAdmins((list) => list.filter((u) => u.id !== person.id));
    });
  };

  const pickLanguage = async (lang) => {
    setOpen(null);
    setSearch("");
    if (lang.code === language) return;
    setLanguage(lang.code);
    await withBusy(async () => {
      const { data } = await callAPI(`/company/${companyId}/language`, {}, "PUT", { language: lang.code });
      if (data?.status) {
        notify("success", data.message);
        return;
      }
      notify("error", failMessage(data));
      // Show the language that is actually stored, not the rejected pick.
      await loadSetting().catch(() => {});
    });
  };

  const toggleArticleLang = (code) => {
    setArticleLangs((prev) => {
      const selection = new Set(prev || []);
      if (selection.has(code)) {
        if (selection.size <= 1) return prev;
        selection.delete(code);
      } else {
        selection.add(code);
      }
      return sortArticleTranslationLangCodes([...selection]);
    });
  };

  const articleLangsInfo = tr(
    "article_translation_target_languages_info",
    "Wähle aus, in welche Sprachen die Artikel dieser Firma übersetzt werden sollen.",
    "Choose which languages this company's articles should be translated into."
  );

  const saveArticleLangs = async () => {
    const languages = normalizeArticleTranslationLanguages(articleLangs);
    setArticleLangsSaving(true);
    try {
      const { data } = await callAPI(`/company/${companyId}/articleTranslationLanguages`, {}, "PUT", { languages });
      if (!data?.status) {
        notify("error", failMessage(data));
        return;
      }
      notify(
        "success",
        tr("article_translation_languages_saved", "Sprachen für Artikelübersetzung gespeichert", "Article translation languages saved")
      );
      setArticleLangs(languages);
    } finally {
      setArticleLangsSaving(false);
    }
  };

  const leaveCompany = async () => {
    if (!(await ask(tr("requet_to_leave", "Firma verlassen", "Request to leave"), strings.leaving_company_text))) return;
    const data = await withBusy(async () => (await callAPI(`/company/${companyId}/leave`, {}, "GET")).data);
    if (!data?.status) {
      notify("error", failMessage(data));
      return;
    }
    store.dispatch({ type: UPDATE_PROJECT_ACTION_FIRE, updateProjectActionFire: true });
    goCockpitCompany("overview");
  };

  const searchLabel = tr("search", "Suche", "Search");
  const emptyPeople = tr("v2_company_no_members", "Noch niemand", "Nobody yet");
  const personSub = (person) => [convertPosition(strings, person.position), deptLine(person)].filter(Boolean).join(" · ");
  const peopleItems = (list, isOn) =>
    list
      .filter((person) => nameMatches(person, search))
      .map((person, index) => ({
        key: `${person.id}-${index}`,
        person,
        name: fullNameOf(person),
        sub: personSub(person),
        on: isOn ? isOn(person) : false,
      }));

  const deptName = [company?.department, company?.location && company.location !== "null" ? company.location : null]
    .filter(Boolean)
    .join("/");
  const removeLabel =
    isDeptAdmin && hasDept && deptName
      ? `${tr("remove_employees_dept", "Mitarbeiter/in entfernen aus", "Remove Employees From")} ${deptName}`
      : tr("remove_employees", "Mitarbeiter/in entfernen", "Remove Employees");
  const languageName = (COMPANY_LANGUAGES.find((lang) => lang.code === language) || {}).name || language;
  const showLanguage = isCompanyAdmin && isAdminPosition;

  const adminRows = [];
  if (teamsOutsideAdminCenter) {
    const teamsSection = ADMIN_CENTER_SECTIONS.teams;
    adminRows.push(
      <SettingsRow
        key="teams"
        icon={teamsSection.icon}
        label={tr(teamsSection.labelKey, teamsSection.de, teamsSection.en)}
        onClick={() => openAdminSection("teams")}
      />
    );
  }
  if (isCompanyAdmin) {
    adminRows.push(
      <SettingsRow
        key="admin"
        icon="admin_panel_settings"
        label={tr("add_remove_admin", "Firmenadmin hinzufügen/entfernen", "Add/Remove company admin position")}
        expanded={open === "admin"}
        onClick={() => toggleRow("admin", () => loadAdmins(company))}
      >
        <MembersPanel
          items={peopleItems(admins, isCompanyAdminEntry)}
          loading={adminsLoading}
          search={search}
          onSearch={setSearch}
          searchPlaceholder={searchLabel}
          emptyText={emptyPeople}
          onPick={toggleCompanyAdmin}
        />
      </SettingsRow>
    );
  }
  if ((isCompanyAdmin || isDeptAdmin) && hasDept) {
    adminRows.push(
      <SettingsRow
        key="deptAdmin"
        icon="shield_person"
        label={tr("add_remove_dep_admin", "Abteilungsadmin hinzufügen/entfernen", "Add/Remove department admin position")}
        expanded={open === "deptAdmin"}
        onClick={() => toggleRow("deptAdmin", () => loadAdmins(company))}
      >
        <MembersPanel
          items={peopleItems(
            admins.filter((person) => positionOf(person) !== POSITION_ADMIN),
            isDeptAdminEntry
          )}
          loading={adminsLoading}
          search={search}
          onSearch={setSearch}
          searchPlaceholder={searchLabel}
          emptyText={emptyPeople}
          onPick={toggleDeptAdmin}
        />
      </SettingsRow>
    );
  }
  if (isAdminPosition && otherAdmins > 0) {
    adminRows.push(
      <SettingsRow
        key="removeSelf"
        icon="remove_moderator"
        label={tr("remove_my_admin_right", "Meine Adminrechte entfernen", "Remove my admin rights")}
        onClick={removeMyAdminRights}
      />
    );
  }
  if (isDeptAdmin) {
    adminRows.push(
      <SettingsRow
        key="removeSelfDept"
        icon="remove_moderator"
        label={tr("remove_my_admin_right", "Meine Adminrechte entfernen", "Remove my admin rights")}
        onClick={removeMyDeptAdminRights}
      />
    );
  }
  if (isCompanyAdmin || isDeptAdmin) {
    adminRows.push(
      <SettingsRow
        key="remove"
        icon="person_remove"
        label={removeLabel}
        expanded={open === "remove"}
        onClick={() => toggleRow("remove", () => loadCoworkers(company))}
      >
        <RemovePanel
          items={peopleItems(coworkers)}
          loading={coworkersLoading}
          search={search}
          onSearch={setSearch}
          searchPlaceholder={searchLabel}
          emptyText={emptyPeople}
          removeLabel={tr("remove_member", "Mitarbeiter/in entfernen", "Remove Member")}
          onRemove={removeEmployee}
        />
      </SettingsRow>
    );
  }

  const companyRows = [];
  if (showLanguage) {
    companyRows.push(
      <SettingsRow
        key="language"
        icon="language"
        label={tr("company_language", "Firmensprache", "Company language")}
        value={languageName}
        expanded={open === "language"}
        onClick={() => toggleRow("language")}
      >
        <LanguagePanel
          value={language}
          label={tr("company_language", "Firmensprache", "Company language")}
          search={search}
          onSearch={setSearch}
          searchPlaceholder={searchLabel}
          emptyText={tr("company_language_none", "Keine Sprache gefunden", "No language found")}
          onPick={pickLanguage}
        />
      </SettingsRow>
    );
  }
  // With the Admin-Panel sections, their Benachrichtigungen / Übersetzungssprachen replace these rows.
  if (isCompanyAdmin && !runsAdminCenter) {
    companyRows.push(
      <SettingsRow
        key="notifications"
        icon="notifications"
        label={tr("notification_settings", "Benachrichtigungen", "Notifications")}
        expanded={open === "notifications"}
        onClick={() => toggleRow("notifications")}
      >
        <CompanyNotificationSettings companyId={companyId} />
      </SettingsRow>
    );
    companyRows.push(
      <SettingsRow
        key="translation"
        icon="translate"
        label={tr("article_translation_target_languages_title", "Artikel-Übersetzungssprachen", "Article translation languages")}
        expanded={open === "translation"}
        onClick={() => toggleRow("translation", loadArticleLangs)}
      >
        <ArticleLanguagesPanel
          note={articleLangsInfo}
          selection={articleLangs}
          onToggle={toggleArticleLang}
          onSave={saveArticleLangs}
          saving={articleLangsSaving}
          saveLabel={tr("article_translation_target_languages_save", "Speichern", "Save")}
        />
      </SettingsRow>
    );
  }

  // The Admin-Panel groups; the company language joins their "Einstellungen".
  const adminSections = adminGroups.map((group) => (
    <SettingsSection
      key={group.key}
      caption={tr(group.labelKey, group.de, group.en)}
      note={group.key === "settings" && showLanguage ? strings.company_language_info : null}
    >
      {group.key === "settings" ? companyRows : null}
      {group.keys.map((key) => {
        const section = ADMIN_CENTER_SECTIONS[key];
        const rawCount = section.countKey && navState ? navState[section.countKey] : null;
        const count = rawCount != null && Number.isFinite(Number(rawCount)) ? String(Number(rawCount)) : null;
        return (
          <SettingsRow
            key={key}
            icon={section.icon}
            label={tr(section.labelKey, section.de, section.en)}
            value={count}
            onClick={() => openAdminSection(key)}
          />
        );
      })}
    </SettingsSection>
  ));

  let body;
  if (company === null) {
    body = <SettingsSkeleton />;
  } else if (company === false) {
    body = (
      <div className="company-setting__state">
        <SettingsNote>
          {tr(
            "company_settings_load_error",
            "Die Firmeneinstellungen konnten nicht geladen werden.",
            "The company settings could not be loaded."
          )}
        </SettingsNote>
        <button
          type="button"
          className="kj-btn kj-btn--secondary kj-btn--sm"
          onClick={() => setReloadKey((n) => n + 1)}
        >
          {tr("cockpit_retry", "Erneut versuchen", "Try again")}
        </button>
      </div>
    );
  } else {
    body = (
      <>
        {runsAdminCenter ? adminSections : null}
        {adminRows.length ? (
          <SettingsSection caption={tr("pro_company_admin_caption_admins", "Verwaltung", "Administration")}>
            {adminRows}
          </SettingsSection>
        ) : null}
        {companyRows.length && !runsAdminCenter ? (
          <SettingsSection
            caption={tr("pro_company_admin_caption_company", "Unternehmen", "Company")}
            note={showLanguage ? strings.company_language_info : null}
          >
            {companyRows}
          </SettingsSection>
        ) : null}
        {isEmployee ? (
          <SettingsSection>
            <SettingsRow
              icon="logout"
              label={tr("leave_the_company", "Die Firma verlassen", "Leave the company")}
              danger
              onClick={leaveCompany}
            />
          </SettingsSection>
        ) : null}
        {!adminRows.length && !companyRows.length && !runsAdminCenter && !isEmployee ? (
          <SettingsNote>
            {tr("company_settings_none", "Für diese Firma gibt es hier keine Einstellungen.", "There are no settings for this company here.")}
          </SettingsNote>
        ) : null}
      </>
    );
  }

  const settingsTitle = tr("cockpit_company_settings", "Firmeneinstellungen", "Company settings");
  return (
    <div className="company_section company_list setting_profile company-setting">
      {busy ? <ApiLoaderOverlay text={strings.Loading} isInternal={isKjuupCompany} /> : null}
      <div className="page-header">
        <button
          type="button"
          className="page-header-back kj-btn kj-btn--icon kj-btn--neutral"
          aria-label={activeSection ? settingsTitle : tr("back", "Zurück", "Back")}
          onClick={activeSection ? backToSettings : () => goCockpitCompany()}
        >
          <span className="material-symbols-outlined material-arrow-left company-setting__back" aria-hidden="true">
            keyboard_arrow_left
          </span>
        </button>
        <h2 className="page-header-title">
          {activeSection ? tr(activeSection.labelKey, activeSection.de, activeSection.en) : settingsTitle}
        </h2>
      </div>
      <div className="company-setting__scroll">
        {activeSection ? (
          <div className="company-admin-center__section">
            <div className="company-admin-center__frame">
              <CompanyAdminEmbed
                key={activeSectionKey}
                companyId={companyId}
                section={activeSection.anchor || ""}
                route={activeSection.route ? `${activeSection.route}?companyId=${companyId}` : ""}
                bare
                className="is-settings"
              />
            </div>
          </div>
        ) : (
          <SettingsStack className="company-setting__stack">{body}</SettingsStack>
        )}
      </div>
    </div>
  );
}

export default CompanySetting;
